<template>
  <v-col cols="12">
    <Loader
      v-if="loading"
      :key="generateUniqueId()"
      :loader-state="loading"
    ></Loader>

    <v-card>
      <v-card-title>Blocks</v-card-title>

      <v-card-text>
        <div class="global-actions">
          <router-link :to="{ name: 'blockCreate' }">
            <v-btn
              small
              color="primary"
              class="mt-5"
            >
              Ajouter un block
            </v-btn>
          </router-link>
        </div>
      </v-card-text>

      <v-row class="mb-5">
        <v-col
          class="tags-search ml-5"
          cols="2"
          sm="2"
        >
          <vue-tags-input
            v-model="tag"
            :tags="tags"
            placeholder="Recherche"
            @tags-changed="newTags => {
              tags = newTags
              search()
            }"
          />
        </v-col>

        <v-col
          cols="12"
          sm="6"
        >
          <v-btn
            class="mt-1"
            small
            color="info"
            @click="search()"
          >
            <v-icon
              small
              class="mr-1"
            >
              {{ icons.mdiDatabaseSearchOutline }}
            </v-icon>
            <i class="fas fa-search"></i>
            Rechercher
          </v-btn>
          <v-btn
            small
            color="info"
            class="ml-2 mt-1"
            @click="reset()"
          >
            <v-icon
              small
              class="mr-1"
            >
              {{ icons.mdiBackupRestore }}
            </v-icon>
            Réinitialisation
          </v-btn>
        </v-col>
      </v-row>

      <div class="clear"></div>

      <v-simple-table>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-uppercase">
                Block
              </th>
              <th class="text-center text-uppercase">
                Type
              </th>
              <th class="text-center text-uppercase">
                Etat
              </th>
              <th class="text-center text-uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody v-if="blocks.length">
            <tr
              v-for="(block, blockIndex) in blocks"
              :key="'b' + blockIndex"
            >
              <td class="font-weight-bold">
                {{ block.title }}
              </td>
              <td class="text-center">
                <v-chip
                  :color="resolveColorType(block.slug)"
                  text-color="white"
                  small
                >
                  {{ resolveType(block.slug) }}
                </v-chip>
              </td>
              <td class="text-center">
                <v-chip
                  v-if="block.enabled"
                  color="success"
                  text-color="white"
                  small
                >
                  Activée
                </v-chip>
                <v-chip
                  v-if="!block.enabled"
                  color="error"
                  text-color="white"
                  small
                >
                  Désactivée
                </v-chip>
              </td>
              <td class="text-center">
                <v-tooltip top>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      color="warning"
                      fab
                      dark
                      x-small
                      v-bind="attrs"
                      :to="{ name: 'blockDetails', params: { blockId: block.id }}"
                    >
                      <v-icon dark>
                        {{ icons.mdiEye }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Consulter</span>
                </v-tooltip>

                <v-tooltip top>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      class="ml-2"
                      color="error"
                      fab
                      dark
                      x-small
                      v-bind="attrs"
                      @click="openDeleteBlockDialog(block)"
                    >
                      <v-icon dark>
                        {{ icons.mdiTrashCan }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Supprimer</span>
                </v-tooltip>
              </td>
            </tr>
          </tbody>
          <tbody v-if="!blocks.length">
            <tr>
              <td>
                <span v-if="loading === false">Aucun éléments.</span>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <DeleteBlockDialog
        :key="Date.now()"
        :delete-block-dialog-state="deleteBlockDialogState"
        :block="blockToDelete"
        @closeDeleteBlockDialog="closeDeleteBlockDialog($event)"
      ></DeleteBlockDialog>
    </v-card>
  </v-col>
</template>

<script>
import { mdiPlay, mdiPen, mdiEye, mdiTrashCan, mdiCheck, mdiCloseCircleOutline, mdiBackupRestore, mdiDatabaseSearchOutline } from '@mdi/js'
import Vue from 'vue'
import DeleteBlockDialog from '@/components/Dialog/ContentDialog/Block/DeleteBlockDialog'
import config from '../../../config'
import Loader from '@/components/Common/Loader'
import VueTagsInput from '@johmun/vue-tags-input'

export default {
  components: {
    DeleteBlockDialog,
    Loader,
    VueTagsInput
  },
  data: () => ({
    nextPage: 1,
    tag: '',
    tags: [],
    blocks: [],
    blockToDelete: null,
    deleteBlockDialogState: false,
    loading: false,
    icons: {
      mdiPlay,
      mdiPen,
      mdiEye,
      mdiTrashCan,
      mdiCheck,
      mdiCloseCircleOutline,
      mdiBackupRestore,
      mdiDatabaseSearchOutline
    }
  }),
  computed: {
    isHandlingBlockScroll () {
      return this.$store.getters.isHandlingBlockScroll
    }
  },
  created () {
    this.getBlocks()

    window.removeEventListener('scroll', this.handleScroll)

    setTimeout(() => {
      window.addEventListener('scroll', this.handleScroll)
    }, 1000)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.handleScroll)
  },
  destroyed () {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll () {
      const wrapperOffsetHeight = document.querySelector('.v-application--wrap').offsetHeight
      const bottomOfWindow = (document.documentElement.scrollTop + window.innerHeight) === wrapperOffsetHeight

      if (bottomOfWindow && !this.isHandlingBlockScroll) {
        this.getBlocks()
      }
    },
    getBlocks () {
      this.loading = true

      const params = {
        page: this.nextPage,
        personalizedOffer: false
      }

      const terms = []

      for (let i = 0; i < this.tags.length; i++) {
        terms.push(this.tags[i].text)
      }

      if (terms.length) {
        params.terms = terms.join(',')
      }

      this.$store.commit('setHandlingBlockScroll', true)

      Vue.prototype.$http
        .get(config.apiUrl + '/api/content/blocks', {
          headers: {
            Authorization: 'Bearer ' + this.$store.getters.getToken
          },
          params: params
        })
        .then(response => {
          if (response.status === 200) {
            const blocks = response.data

            if (blocks && blocks.length) {
              this.blocks.push(...blocks)

              this.nextPage = this.nextPage + 1
            }
          }

          this.loading = false
          this.$store.commit('setHandlingBlockScroll', false)
        })
        .catch(e => {
          const response = e.response

          if (response.status === 400 || response.status === 404) {
            const data = response.data
            this.errors = data.violations
          }

          this.valid = true
          this.loading = false
          this.$store.commit('setHandlingBlockScroll', false)
        })
    },
    search () {
      const params = {
        page: 1
      }

      const terms = []

      for (let i = 0; i < this.tags.length; i++) {
        terms.push(this.tags[i].text)
      }

      if (terms.length) {
        params.terms = terms.join(',')
      }

      Vue.prototype.$http
        .get(config.apiUrl + '/api/content/blocks', {
          headers: {
            Authorization: 'Bearer ' + this.$store.getters.getToken
          },
          params: params
        })
        .then(response => {
          if (response.status === 200) {
            this.blocks = response.data

            this.nextPage = 2
          }
        })
        .catch(() => {
        })
    },
    reset () {
      this.tag = ''
      this.tags = []

      this.search()
    },
    resolveType (slug) {
      if (slug.includes('commitment')) {
        return 'Block engagement'
      } else if (slug.includes('header-camping-map')) {
        return 'Header carte'
      } else if (slug.includes('promotions')) {
        return 'Block promotion'
      } else if (slug.includes('header')) {
        return 'Header de page'
      } else {
        return 'Block'
      }
    },
    resolveColorType (slug) {
      if (slug.includes('commitment')) {
        return 'success'
      } else if (slug.includes('header-camping-map')) {
        return 'info'
      } else if (slug.includes('promotions')) {
        return 'secondary'
      } else if (slug.includes('header')) {
        return 'primary'
      } else {
        return 'warning'
      }
    },
    openDeleteBlockDialog (block) {
      this.deleteBlockDialogState = true
      this.blockToDelete = block
    },
    closeDeleteBlockDialog (deleteState) {
      if (deleteState === true) {
        this.blocks = this.blocks.filter(element => element.id !== this.blockToDelete.id)
      }

      this.deleteBlockDialogState = false
    }
  }
}
</script>
